import { useEffect, useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { ChevronLeft, ChevronRight, Sparkles, Sun, Sunrise, Moon, Inbox, RefreshCw } from "lucide-react";
import { addDays, format } from "date-fns";
import { it } from "date-fns/locale";
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors, useDroppable } from "@dnd-kit/core";
import { ISO, fmtLong, slotOfDay, parseISODate } from "@/lib/date";
import { pickForSeed } from "@/lib/quotes";
import { tasksForDate, unscheduledTasks, useStore, moveTaskSmart, setJournal, allCategories, workedMin } from "@/lib/store";
import AddTaskForm from "@/components/AddTaskForm";
import TaskItem from "@/components/TaskItem";
import TimerBar from "@/components/TimerBar";
import PrioritiesCard from "@/components/PrioritiesCard";
import RecurrenceScopeDialog from "@/components/RecurrenceScopeDialog";
import { useNavigate } from "react-router-dom";

function DroppableSection({ id, children, className = "" }) {
  const { setNodeRef, isOver } = useDroppable({ id, data: { type: "section" } });
  return (
    <div ref={setNodeRef} className={`${className} ${isOver ? "ring-2 ring-[#A3B897]/50 ring-offset-2 ring-offset-[#FDFDFB]" : ""}`}>
      {children}
    </div>
  );
}

export default function DayPage() {
  const store = useStore();
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [quoteSeedOverride, setQuoteSeedOverride] = useState(0);
  const [calMonth, setCalMonth] = useState(new Date());
  useEffect(() => setCalMonth(date), [date]);
  const busyDays = useMemo(() => {
    const first = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1);
    return Array.from({ length: 49 }, (_, i) => addDays(first, i - 7))
      .filter((d) => tasksForDate(ISO(d)).length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calMonth, store.tasks]);

  const iso = ISO(date);
  const tasksAll = tasksForDate(iso);
  const inbox = unscheduledTasks();

  const filtered = useMemo(() => {
    return tasksAll.filter((t) => {
      if (filterCat !== "all" && t.categoryId !== filterCat) return false;
      if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [tasksAll, filterCat, search]);

  const morning = filtered.filter((t) => t.time && slotOfDay(t.time) === "mattina");
  const afternoon = filtered.filter((t) => t.time && slotOfDay(t.time) === "pomeriggio");
  const evening = filtered.filter((t) => t.time && slotOfDay(t.time) === "sera");
  const noTime = filtered.filter((t) => !t.time);

  const quote = useMemo(
    () => pickForSeed(store.settings.quotes, iso + "|q|" + quoteSeedOverride),
    [store.settings.quotes, iso, quoteSeedOverride]
  );

  const totalMin = filtered.reduce((s, t) => s + (t.durationMin || 0), 0);
  const remainingMin = filtered.filter((t) => !t.done).reduce((s, t) => s + (t.durationMin || 0), 0);
  const doneMin = filtered.reduce((s, t) => s + workedMin(t), 0);
  const donePct = filtered.length ? Math.round((filtered.filter((t) => t.done).length / filtered.length) * 100) : 0;

  const journalEntry = store.journal[iso] || { best: "", learned: "" };

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } })
  );
  const [pendingMove, setPendingMove] = useState(null);
  function applyMove(task, occurrenceDate, targetDate, targetTime) {
    const isRecurring = task.recurrence && task.recurrence !== "none";
    if (isRecurring) setPendingMove({ task, occurrenceDate, targetDate, targetTime });
    else moveTaskSmart(task, occurrenceDate, targetDate, targetTime, "single");
  }
  function onDragEnd(evt) {
    const { active, over } = evt;
    if (!over) return;
    const overData = over.data.current;
    const task = active.data.current?.task;
    if (!task) return;
    const occurrenceDate = active.data.current?.occurrenceDate || task.date;
    if (overData?.type === "section" && over.id.startsWith("day-section-")) {
      // Section id format: day-section-<slot>-<isoDate>
      const rest = over.id.replace("day-section-", "");
      const [slot] = rest.split("-", 1);
      const slotToTime = { mattina: "09:00", pomeriggio: "15:00", sera: "20:00", notime: null };
      const newTime = slotToTime[slot];
      applyMove(task, occurrenceDate, iso, newTime === null ? null : newTime);
    } else if (overData?.type === "inbox") {
      applyMove(task, occurrenceDate, null, undefined);
    }
  }

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div className="flex flex-col gap-6" data-testid="day-page">
        {/* Header */}
        <div className="flex flex-col items-center gap-3">
          <div className="flex items-center gap-2 md:gap-3 w-full justify-center">
            <button className="p-2 rounded-full hover:bg-[#F3F5F0] tl-icon-btn bg-white flex-shrink-0" onClick={() => setDate(addDays(date, -1))} data-testid="day-prev">
              <ChevronLeft size={20} strokeWidth={1.75} />
            </button>
            <h1 className="font-serif-display font-semibold text-2xl sm:text-3xl md:text-5xl text-[#3B443D] capitalize text-center leading-tight min-w-0" data-testid="day-title">
              {fmtLong(date)}
            </h1>
            <button className="p-2 rounded-full hover:bg-[#F3F5F0] tl-icon-btn bg-white flex-shrink-0" onClick={() => setDate(addDays(date, 1))} data-testid="day-next">
              <ChevronRight size={20} strokeWidth={1.75} />
            </button>
          </div>
          <div className="flex items-center gap-2 md:gap-3 px-2 text-center">
            <div className="font-serif-display italic font-semibold text-base md:text-2xl text-[#8FA087]" data-testid="day-quote">“{quote}”</div>
            <button className="text-[#6A7368] hover:text-[#8FA087] flex-shrink-0" onClick={() => setQuoteSeedOverride((n) => n + 1)} aria-label="Cambia frase" data-testid="day-quote-refresh">
              <RefreshCw size={14} strokeWidth={1.5} />
            </button>
            {ISO(date) !== ISO(new Date()) && (
              <button className="text-xs text-[#A3B897] hover:underline flex-shrink-0" onClick={() => setDate(new Date())} data-testid="day-today">
                oggi
              </button>
            )}
          </div>
        </div>

        {/* Stats strip */}
        <div className="tl-paper p-4 grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat label="attività di oggi" value={filtered.length} />
          <Stat label="completati" value={filtered.filter((t) => t.done).length} />
          <Stat label="stima rimasta" value={fmtMin(remainingMin)} />
          <Stat label="tempo speso" value={fmtMin(doneMin) || "—"} />
          <div>
            <div className="tl-label mb-1">Avanzamento</div>
            <div className="flex items-center gap-2">
              <div className="h-2 flex-1 bg-[#F3F5F0] rounded-full overflow-hidden">
                <div className="h-full bg-[#A3B897] transition-[width] duration-500" style={{ width: `${donePct}%` }} />
              </div>
              <span className="text-xs font-medium text-[#58635B] tabular-nums">{donePct}%</span>
            </div>
          </div>
        </div>

        {/* Add + Filters */}
        <AddTaskForm defaultDate={iso} testIdPrefix="day-add" collapsible />
        <div className="flex flex-wrap gap-2 items-center">
          <select
            data-testid="day-filter-category"
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="px-4 py-2 bg-white border border-[#E4E7E1] rounded-full text-sm text-[#3B443D] focus:outline-none focus:border-[#A3B897]"
          >
            <option value="all">Tutte le categorie</option>
            {allCategories(store).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 min-w-0">
          {/* Schedule */}
          <div className="lg:col-span-2 flex flex-col gap-4 min-w-0">
            {noTime.length > 0 && (
              <SectionCard id={`day-section-notime-${iso}`} title="Senza orario" icon={<Sparkles size={14} strokeWidth={1.5} />} count={noTime.length}>
                {noTime.map((t) => <TaskItem key={`${t.id}-${t._occurrenceDate || t.date}`} task={t} />)}
              </SectionCard>
            )}
            <SectionCard id={`day-section-mattina-${iso}`} title="Mattina" icon={<Sunrise size={14} strokeWidth={1.5} />} count={morning.length}>
              {morning.length === 0 ? <Empty text="Niente in programma." /> : morning.map((t) => <TaskItem key={`${t.id}-${t._occurrenceDate || t.date}`} task={t} />)}
            </SectionCard>
            <SectionCard id={`day-section-pomeriggio-${iso}`} title="Pomeriggio" icon={<Sun size={14} strokeWidth={1.5} />} count={afternoon.length}>
              {afternoon.length === 0 ? <Empty text="Niente in programma." /> : afternoon.map((t) => <TaskItem key={`${t.id}-${t._occurrenceDate || t.date}`} task={t} />)}
            </SectionCard>
            <SectionCard id={`day-section-sera-${iso}`} title="Sera" icon={<Moon size={14} strokeWidth={1.5} />} count={evening.length}>
              {evening.length === 0 ? <Empty text="Niente in programma." /> : evening.map((t) => <TaskItem key={`${t.id}-${t._occurrenceDate || t.date}`} task={t} />)}
            </SectionCard>
          </div>

          {/* Right column */}
          <div className="flex flex-col gap-4 min-w-0">
            <TimerBar />
            <div className="tl-paper p-2 flex justify-center" data-testid="day-month-calendar">
              <Calendar
                mode="single"
                locale={it}
                weekStartsOn={1}
                selected={date}
                month={calMonth}
                onMonthChange={setCalMonth}
                onSelect={(d) => d && setDate(d)}
                labels={{ labelNext: () => "Mese successivo", labelPrevious: () => "Mese precedente" }}
                modifiers={{ hasTasks: busyDays }}
                modifiersClassNames={{ hasTasks: "tl-has-tasks" }}
              />
            </div>
            <PrioritiesCard dateISO={iso} />
            <div className="tl-paper-cream p-5" data-testid="journal-card">
              <div className="tl-label flex items-center gap-2"><Sparkles size={12} strokeWidth={1.5} /> Diario di oggi</div>
              <div className="mt-3">
                <label className="text-xs text-[#58635B] block mb-1">La cosa bella di oggi</label>
                <textarea
                  data-testid="journal-best"
                  rows={2}
                  value={journalEntry.best}
                  onChange={(e) => setJournal(iso, { best: e.target.value })}
                  placeholder="Un momento da ricordare…"
                  className="w-full px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm focus:outline-none focus:border-[#A3B897]"
                />
              </div>
              <div className="mt-3">
                <label className="text-xs text-[#58635B] block mb-1">La cosa che ho imparato</label>
                <textarea
                  data-testid="journal-learned"
                  rows={2}
                  value={journalEntry.learned}
                  onChange={(e) => setJournal(iso, { learned: e.target.value })}
                  placeholder="Una piccola lezione…"
                  className="w-full px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm focus:outline-none focus:border-[#A3B897]"
                />
              </div>
            </div>

            <InboxCard tasks={inbox} onOpenWeek={() => navigate("/settimana")} />
            <div className="tl-paper p-3" data-testid="search-card">
              <input
                data-testid="day-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca nelle attività…"
                className="w-full px-4 py-2 bg-[#FDFDFB] border border-[#E4E7E1] rounded-full text-sm text-[#3B443D] placeholder:text-[#6A7368] focus:outline-none focus:border-[#A3B897]"
              />
            </div>
          </div>
        </div>
      </div>
      <RecurrenceScopeDialog
        open={!!pendingMove}
        label="Spostare l'attività per..."
        onCancel={() => setPendingMove(null)}
        onChoose={(scope) => {
          const p = pendingMove;
          setPendingMove(null);
          if (p) moveTaskSmart(p.task, p.occurrenceDate, p.targetDate, p.targetTime, scope);
        }}
      />
    </DndContext>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-serif-display text-3xl text-[#3B443D] leading-none">{value ?? "—"}</div>
      <div className="tl-label mt-1">{label}</div>
    </div>
  );
}

function SectionCard({ id, title, icon, count, children }) {
  const { setNodeRef, isOver } = useDroppable({ id, data: { type: "section" } });
  return (
    <div ref={setNodeRef} className={`tl-paper p-4 min-w-0 overflow-hidden ${isOver ? "ring-2 ring-[#A3B897]/50" : ""}`} data-testid={id}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 tl-label">{icon}{title}</div>
        <div className="text-xs text-[#6A7368]">{count} attività</div>
      </div>
      <div className="flex flex-col min-w-0">{children}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-sm text-[#6A7368] py-3 px-3 italic">{text}</div>;
}

function InboxCard({ tasks, onOpenWeek }) {
  const { setNodeRef, isOver } = useDroppable({ id: "inbox-drop", data: { type: "inbox" } });
  return (
    <div ref={setNodeRef} className={`tl-paper p-5 ${isOver ? "ring-2 ring-[#A3B897]/50" : ""}`} data-testid="inbox-card">
      <div className="flex items-center gap-2 tl-label"><Inbox size={12} strokeWidth={1.5} />Prossimamente</div>
      <div className="text-xs text-[#6A7368] mt-1">Trascina qui le attività senza data, o portali in un giorno dalla settimana.</div>
      <div className="mt-3">
        <AddTaskForm defaultDate={null} compact testIdPrefix="inbox-add" />
      </div>
      <div className="mt-3 flex flex-col">
        {tasks.length === 0 ? (
          <Empty text="Nessuna attività in attesa." />
        ) : tasks.slice(0, 8).map((t) => (
          <TaskItem key={t.id} task={t} showTime={false} />
        ))}
      </div>
      <button type="button" onClick={onOpenWeek} className="mt-2 text-xs text-[#A3B897] hover:underline" data-testid="inbox-open-week">
        Vai alla settimana →
      </button>
    </div>
  );
}

function fmtMin(min) {
  if (!min) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
